import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Alert,
  Typography,
  Space,
  Tag,
  Collapse,
} from 'antd';
import { DatabaseOutlined, CameraOutlined } from '@ant-design/icons';
import { listColeccionesApi, promoteItemToCatalogApi } from 'api/hallazgoFlujo';
import ColeccionItemImageUpload from 'components/forms/ColeccionItemImageUpload';
import useTenantFlags from 'hooks/useTenantFlags';

// Modal para guardar un item libre del presupuesto al catálogo (coleccion_items).
// El usuario elige a qué colección agregarlo y el tipo del item. Tras crear,
// el parent recibe el nuevo item y enlaza localmente el budgetitem
// (seteando id_coleccion_item en el Form del BudgetEditor).

const TIPO_OPTIONS = [
  { value: 'PRODUCTO', label: 'Producto' },
  { value: 'SERVICIO', label: 'Servicio' },
  { value: 'OTRO', label: 'Otro' },
];

function inferTipoFromColeccion(col) {
  if (!col) return 'PRODUCTO';
  if (col.type === 'SERVICIOS') return 'SERVICIO';
  if (col.type === 'PRODUCTOS') return 'PRODUCTO';
  return 'OTRO';
}

function SaveItemToCatalogModal({ open, onClose, onSaved, snapshot }) {
  // snapshot: { nombre, descripcion, precio, moneda } extraído del item del presupuesto
  const [form] = Form.useForm();
  const [colecciones, setColecciones] = useState([]);
  const [loadingColecciones, setLoadingColecciones] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Foto opcional: queda pendiente en el componente hasta que se crea el item,
  // despues se sube via imageUploadRef.uploadPending(newItemId).
  const imageUploadRef = useRef(null);
  const [pendingImageMarker, setPendingImageMarker] = useState(null);
  const { readOnly } = useTenantFlags();

  // Solo colecciones útiles para items de presupuesto (excluir TEXTOS/TEMPLATES)
  const coleccionesFiltered = useMemo(
    () => colecciones.filter((c) => c.type === 'PRODUCTOS' || c.type === 'SERVICIOS' || !c.type),
    [colecciones]
  );

  useEffect(() => {
    if (!open) return;
    setError(null);
    setLoadingColecciones(true);
    listColeccionesApi()
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setColecciones(list);
        const filtered = list.filter(
          (c) => c.type === 'PRODUCTOS' || c.type === 'SERVICIOS' || !c.type
        );
        const defaultCol = filtered.find((c) => c.type === 'PRODUCTOS') || filtered[0] || null;
        form.setFieldsValue({
          nombre: snapshot?.nombre || '',
          descripcion: snapshot?.descripcion || '',
          precio: snapshot?.precio ?? null,
          costo: snapshot?.costo ?? null,
          moneda: snapshot?.moneda || 'CLP',
          id_coleccion: defaultCol?.id,
          tipo_item: inferTipoFromColeccion(defaultCol),
        });
      })
      .catch(() => setError('No se pudieron cargar las colecciones'))
      .finally(() => setLoadingColecciones(false));
  }, [open, snapshot, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      setError(null);
      const res = await promoteItemToCatalogApi({
        nombre: values.nombre?.trim(),
        descripcion: values.descripcion || null,
        precio: values.precio ?? null,
        costo: values.costo ?? null,
        moneda: values.moneda || 'CLP',
        tipo_item: values.tipo_item || 'PRODUCTO',
        id_coleccion: values.id_coleccion,
      });
      if (!res || res.status === 'client' || !res.item) {
        setError(res?.message || 'No se pudo guardar en el catálogo');
        return;
      }
      // Si el usuario adjunto una foto antes de guardar, subirla ahora que el
      // item existe. Un fallo aca no revierte la creacion del item.
      let finalItem = res.item;
      if (imageUploadRef.current?.hasPending?.()) {
        try {
          const key = await imageUploadRef.current.uploadPending(res.item.id);
          if (key) finalItem = { ...res.item, image_key: key };
        } catch (_imgErr) {
          setError(
            'El item se guardo, pero no pudimos subir la foto. Podes agregarla despues desde el catalogo.'
          );
        }
      }
      onSaved?.(finalItem);
      onClose?.();
    } catch (_validationOrNetwork) {
      // validateFields ya pinta los campos; errores de red caen a setError arriba
    } finally {
      setSubmitting(false);
    }
  };

  const handleColeccionChange = (id) => {
    const col = colecciones.find((c) => c.id === id);
    form.setFieldValue('tipo_item', inferTipoFromColeccion(col));
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText="Guardar en catálogo"
      cancelText="Cancelar"
      confirmLoading={submitting}
      okButtonProps={{ disabled: readOnly }}
      title={
        <Space>
          <DatabaseOutlined />
          <span>Guardar item en catálogo</span>
        </Space>
      }
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Se agregará como item reutilizable. La próxima vez aparecerá en el autocomplete del
        catálogo.
      </Typography.Paragraph>

      {error ? <Alert type="error" showIcon style={{ marginBottom: 12 }} message={error} /> : null}

      <Form form={form} layout="vertical">
        <Form.Item
          name="id_coleccion"
          label="Colección destino"
          rules={[{ required: true, message: 'Elegí una colección' }]}
        >
          <Select
            loading={loadingColecciones}
            placeholder="Seleccioná una colección"
            onChange={handleColeccionChange}
            options={coleccionesFiltered.map((c) => ({
              value: c.id,
              label: (
                <Space>
                  <span>{c.nombre}</span>
                  {c.type ? (
                    <Tag
                      color={c.type === 'SERVICIOS' ? 'blue' : 'default'}
                      style={{ fontSize: 10, marginInlineEnd: 0 }}
                    >
                      {c.type}
                    </Tag>
                  ) : null}
                </Space>
              ),
            }))}
          />
        </Form.Item>

        <Form.Item
          name="nombre"
          label="Nombre"
          rules={[{ required: true, message: 'El nombre es obligatorio' }]}
        >
          <Input maxLength={255} placeholder="Nombre del item en el catálogo" />
        </Form.Item>

        <Form.Item name="descripcion" label="Descripción (opcional)">
          <Input.TextArea rows={2} placeholder="Detalle adicional…" />
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px', gap: 12 }}>
          <Form.Item name="tipo_item" label="Tipo">
            <Select options={TIPO_OPTIONS} />
          </Form.Item>
          <Form.Item name="moneda" label="Moneda">
            <Select
              options={[
                { value: 'CLP', label: 'CLP' },
                { value: 'UF', label: 'UF' },
                { value: 'USD', label: 'USD' },
              ]}
            />
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item name="precio" label="Precio de venta" tooltip="Cuánto se le cobra al cliente">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
          </Form.Item>
          <Form.Item
            name="costo"
            label="Costo"
            tooltip="Cuánto nos cuesta el item (para calcular margen)"
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
          </Form.Item>
        </div>

        <Collapse
          ghost
          items={[
            {
              key: 'foto',
              label: (
                <Space size={4}>
                  <CameraOutlined />
                  <span>Agregar foto (opcional)</span>
                </Space>
              ),
              children: (
                <ColeccionItemImageUpload
                  ref={imageUploadRef}
                  value={pendingImageMarker}
                  onChange={setPendingImageMarker}
                  itemId={null}
                />
              ),
            },
          ]}
        />
      </Form>
    </Modal>
  );
}

export default SaveItemToCatalogModal;
